import { html, type Html, Page } from 'rune-ts';
import klass from './AdResFinish.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { footer_links } from '../../components/cells/Footer/constant';
import { getResizedUrl } from '../../../shared/util/image';
import { font } from '@services/MarppleShop/shared/typography';
import { classes } from '@services/MarppleShop/shared/util';
import { pipe } from '@fxts/core';
import { convertNewLinesToBr } from '@services/MarppleShop/shared/util/convertNewLinesToBr.ts';

const kakaoTalkIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="14"
  height="15"
  viewBox="0 0 14 15"
  fill="none"
>
  <g clip-path="url(#clip0_12988_196)">
    <path
      d="M7.00069 0.5C3.41073 0.5 0.5 3.19212 0.5 6.51259C0.5 8.96547 1.52708 10.1355 2.93363 11.2025L2.94051 11.2052V14.083C2.94051 14.2191 3.09587 14.2961 3.20449 14.2163L5.66975 12.3849L5.72337 12.4083C6.13723 12.4839 6.56346 12.5252 7.00069 12.5252C10.5906 12.5252 13.5014 9.83305 13.5014 6.51259C13.5014 3.19212 10.5906 0.5 7.00069 0.5Z"
      fill="black"
    />
    <path
      d="M10.6692 5.7758C10.9098 6.02328 11.0308 6.35877 11.0308 6.79325V8.84877H10.1673V6.90049C10.1673 6.45226 9.90608 6.21027 9.5046 6.21027C9.05637 6.21027 8.76763 6.48526 8.76763 7.04073V8.84877H7.90417V4.08325H8.76763V5.87617C8.97525 5.56131 9.30936 5.40731 9.76584 5.40731C10.1343 5.40731 10.4354 5.52831 10.6692 5.7758Z"
      fill="#FEE500"
    />
    <path
      d="M6.16762 7.06532C6.004 7.65516 5.45815 8.03602 4.82156 8.03602C3.98698 8.03602 3.29263 7.39393 3.29263 6.50572C3.29263 5.61751 3.9031 4.96304 4.82156 4.96304C5.47053 4.96304 6.03425 5.35352 6.17724 5.98049H7.1177C6.89771 4.82692 6.0095 4.06934 4.82156 4.06934C3.45488 4.06934 2.3728 5.08816 2.3728 6.50572C2.3728 7.92328 3.45625 8.9421 4.82156 8.9421C5.99713 8.9421 6.87984 8.19826 7.11082 7.06532H6.16762Z"
      fill="#FEE500"
    />
  </g>
  <defs>
    <clipPath id="clip0_12988_196">
      <rect width="13" height="13.7493" fill="white" transform="translate(0.5 0.5)" />
    </clipPath>
  </defs>
</svg>`;

export type AdResFinishPageData = {
  layout_options: BaseLayoutOptions;
  email: string;
};

export class AdResFinishPage extends Page<AdResFinishPageData> {
  protected override template({ layout_options, email }: AdResFinishPageData): Html {
    const { is_mobile } = layout_options;
    const texts = {
      title: html.preventEscape(ET(`mps2::ad_res::finish_title${is_mobile ? '_mo' : ''}`)),
      description_1: ET('mps2::ad_res::finish_description_1'),
      description_2: html.preventEscape(ET('mps2::ad_res::finish_description_2')),
      info_desc: html.preventEscape(ET(`mps2::ad_res::finish_info_desc${is_mobile ? '_mo' : ''}`)),
    };
    const body = html`
      <div class="${klass.page}">
        <div class="${klass.container}">
          <div class="${klass.image_wrapper}">
            <img
              class="${klass.image}"
              src="${getResizedUrl({
                url: '//s3.marpple.co/files/u_4648126/2024/10/original/42b8927f0d6d4d19e6f6a696a7a5e57afaa64df01.png',
                width: 200 * 2,
                quality: 85,
              })}"
              alt="MarppleShop creator apply finish image"
            />
          </div>
          <div class="${klass.title} ${font('24_bold', '20_bold')}">${texts.title}</div>
          <div class="${klass.email} ${font('20_bold', '16_bold')}">${email}</div>
          <div class="${klass.content}">
            <div class="${font('16_medium', '14_medium')}">${texts.description_1}</div>
            <div class="${font('16_medium', '14_medium')}">${texts.description_2}</div>
          </div>
          <div class="${klass.info} ${font('14_medium', '12_medium')}">${texts.info_desc}</div>
        </div>

        <div class="${klass.kakao_guide}">
          <div class="${klass.kakao_guide_contents}">
            <p class="${classes(klass.kakao_guide_description, font('14_medium', '13_medium'))}">
              ${pipe(
                ET(
                  is_mobile
                    ? 'ad_res_finish_page::kakao_guide::description_mo'
                    : 'ad_res_finish_page::kakao_guide::description',
                ),
                convertNewLinesToBr,
                html.preventEscape,
              )}
            </p>

            <a class="${klass.button_40}" href="https://pf.kakao.com/_EfTDn" target="_blank">
              <span class="${klass.button_40_icon}">${kakaoTalkIcon}</span>

              <span class="${classes(klass.button_40_text, font('14_medium'))}">
                ${pipe(
                  ET(
                    is_mobile
                      ? 'ad_res_finish_page::kakao_guide::button_40_text_mo'
                      : 'ad_res_finish_page::kakao_guide::button_40_text',
                  ),
                  convertNewLinesToBr,
                  html.preventEscape,
                )}
              </span>
            </a>
          </div>
        </div>
      </div>
    `;

    return html`
      <div>
        ${new LayoutView(
          {
            body,
            footer_data: { ...footer_links, lang: ET.lang, is_mobile },
          },
          {
            require_top_arrow_button: false,
            full_width: true,
            has_bottom_tab_bar: false,
            selected_bottom_tab_idx: -1,
            ...layout_options,
          },
        )}
      </div>
    `;
  }
}
